import { memo, useCallback, useEffect, useMemo } from 'react';
import { Layouts, Responsive, WidthProvider } from 'react-grid-layout';
import { Widget } from 'graphql-common';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import CircleLoaderWrapper from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import { ApolloError } from '@apollo/client';
import getStatusFromError from '@lib/utils/getStatusFromError';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { LocalWidget, GridLayoutItem } from 'routes/Home/hooks/useWidgetEditor';
import useWidgetGrid from 'routes/Home/hooks/useWidgetGrid';
import * as config from '../../constants';
import GridLayoutItemComponent from '../GridLayoutItem/GridLayoutItem';
import WidgetContent, {
  WidgetGrid as WidgetGridType,
} from '../WidgetContent/WidgetContent';
import WidgetsViewEmptyState from './WidgetsViewEmptyState';
import WidgetsEditEmptyState from './WidgetsEditEmptyState';
import styles from './Widgets.module.scss';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface WidgetsProps {
  widgets: Widget[];
  widgetsLoading: boolean;
  widgetsError?: ApolloError;
  isViewOnly: boolean;
  isEditMode: boolean;
  isDefault: boolean;
  onEnterEditMode: () => void;
  onAddWidget: () => void;
  // Edit mode props
  localWidgets: LocalWidget[];
  localGrid: GridLayoutItem[];
  onLayoutChange: (layouts: GridLayoutItem[]) => void;
  onEditWidget: (widgetId: string) => void;
  onRemoveWidget: (widgetId: string) => void;
  saveLoading?: boolean;
}

function Widgets({
  widgets,
  widgetsLoading,
  widgetsError,
  isViewOnly,
  isEditMode,
  isDefault,
  onEnterEditMode,
  onAddWidget,
  localWidgets,
  localGrid,
  onLayoutChange,
  onEditWidget,
  onRemoveWidget,
  saveLoading,
}: WidgetsProps) {
  // Use custom hook for widget grid logic (filtering, layouts, visibility)
  const { displayWidgets, layouts, widgetsGrid } = useWidgetGrid({
    widgets,
    localWidgets,
    localGrid,
    isEditMode,
  });

  // Initialize localGrid when entering edit mode with existing grid
  useEffect(() => {
    if (isEditMode && localGrid.length === 0 && widgetsGrid.length > 0) {
      onLayoutChange(widgetsGrid);
    }
  }, [isEditMode, localGrid.length, widgetsGrid, onLayoutChange]);

  const handleLayoutChange = useCallback(
    (_: unknown, allLayouts: Layouts) => {
      if (!isEditMode) return;
      onLayoutChange(allLayouts.lg || []);
    },
    [isEditMode, onLayoutChange],
  );

  // Memoize grid children to prevent unnecessary re-renders
  const gridChildren = useMemo(
    () =>
      displayWidgets.map((widget) => {
        const widgetId =
          'tempId' in widget
            ? widget.id || (widget as LocalWidget).tempId
            : widget.id;
        // Get grid from layouts for this widget
        const gridItem = layouts.lg?.find(
          (item: GridLayoutItem) => item.i === widgetId,
        );
        const widgetGridData: WidgetGridType | undefined = gridItem
          ? { x: gridItem.x, y: gridItem.y, w: gridItem.w, h: gridItem.h }
          : undefined;
        return (
          <div key={widgetId}>
            <GridLayoutItemComponent
              isEditMode={isEditMode}
              widgetKind={widget.kind}
              onEdit={() => onEditWidget(widgetId || '')}
              onRemove={() => onRemoveWidget(widgetId || '')}
            >
              <WidgetContent
                widget={widget}
                grid={widgetGridData}
                isDefaultHomePage={isDefault}
              />
            </GridLayoutItemComponent>
          </div>
        );
      }),
    [
      displayWidgets,
      layouts.lg,
      isEditMode,
      isDefault,
      onEditWidget,
      onRemoveWidget,
    ],
  );

  if (widgetsLoading) {
    return <CircleLoader />;
  }

  if (widgetsError) {
    return (
      <ErrorPage
        status={getStatusFromError(widgetsError)}
        description={widgetsError?.message}
      />
    );
  }

  if (!displayWidgets.length) {
    if (isEditMode) {
      return (
        <WidgetsEditEmptyState
          isDefault={isDefault}
          onAddWidget={onAddWidget}
        />
      );
    }

    return (
      <WidgetsViewEmptyState
        isViewOnly={isViewOnly}
        isDefault={isDefault}
        onCustomize={onEnterEditMode}
      />
    );
  }

  // Render widgets in grid layout
  return (
    <CircleLoaderWrapper isLoading={saveLoading}>
      <div className={styles.widgets}>
        <ResponsiveGridLayout
          breakpoints={config.breakpoints}
          className={styles.gridLayout}
          cols={config.cols}
          compactType="vertical"
          draggableHandle={`.${config.draggableHandle}`}
          isDraggable={isEditMode}
          isResizable={isEditMode}
          layouts={layouts}
          margin={config.margin}
          measureBeforeMount={false}
          onLayoutChange={handleLayoutChange}
          padding={config.padding}
          preventCollision={false}
          resizeHandles={config.resizeHandles}
          rowHeight={config.rowHeight}
          useCSSTransforms
        >
          {gridChildren}
        </ResponsiveGridLayout>
      </div>
    </CircleLoaderWrapper>
  );
}

export default memo(Widgets);
